import { useMemo } from "react";

import { useSimMode } from "../../sim/SimModeContext";
import type { WebsiteConfig } from "../../types/config";
import type { StakingStatsResponse } from "../../types/protocol";
import { formatAdvertisedApy, resolveAdvertisedApy } from "../../utils/apy";
import { useWebsiteConfig } from "../api/useWebsiteConfig";

/**
 * Return type for useAdvertisedApy hook
 */
export interface UseAdvertisedApyResult {
    /** Resolved APY as percentage number (e.g., 12.7), or null if unavailable */
    apy: number | null;
    /** Formatted APY string (e.g., "12.7%"), or "-" if unavailable */
    formattedApy: string;
    /** Whether config or stats are still loading */
    isLoading: boolean;
    /** Error from config fetch (stats errors handled separately) */
    error: Error | null;
    /** Raw config data */
    config: WebsiteConfig | undefined;
}

/**
 * Composable hook that combines website config and staking stats
 * to resolve the advertised APY.
 *
 * This hook:
 * 1. Fetches website config (APYOverride, APYCeiling)
 * 2. Takes staking stats as a parameter (from useStakingStats)
 * 3. Resolves APY using waterfall logic
 * 4. Returns both raw number and formatted string
 *
 * @param stakingStats - Staking stats from useStakingStats hook
 * @param isStatsLoading - Loading state from useStakingStats
 *
 * @example
 * ```tsx
 * const { data: stats, isLoading: statsLoading } = useStakingStats();
 * const { apy, formattedApy, isLoading } = useAdvertisedApy(stats, statsLoading);
 *
 * if (isLoading) return <Skeleton />;
 * return <span>{formattedApy}</span>; // "12.7%"
 * ```
 */
const SIM_APY_RESULT: UseAdvertisedApyResult = {
    apy: 7.1,
    formattedApy: "7.1%",
    isLoading: false,
    error: null,
    config: undefined
};

export function useAdvertisedApy(
    stakingStats?: StakingStatsResponse,
    isStatsLoading: boolean = false
): UseAdvertisedApyResult {
    const sim = useSimMode();
    const useImplementation = sim.enabled ? useSimAdvertisedApy : useLiveAdvertisedApy;
    return useImplementation(stakingStats, isStatsLoading);
}

function useSimAdvertisedApy(): UseAdvertisedApyResult {
    return SIM_APY_RESULT;
}

function useLiveAdvertisedApy(
    stakingStats: StakingStatsResponse | undefined,
    isStatsLoading: boolean
): UseAdvertisedApyResult {
    const { data: config, isLoading: isConfigLoading, error: configError } = useWebsiteConfig();

    const isLoading = isConfigLoading || isStatsLoading;

    const apy = useMemo(() => {
        if (isLoading) return null;
        return resolveAdvertisedApy(config ?? {}, stakingStats);
    }, [config, stakingStats, isLoading]);

    const formattedApy = useMemo(() => {
        return formatAdvertisedApy(apy);
    }, [apy]);

    return {
        apy,
        formattedApy,
        isLoading,
        error: configError as Error | null,
        config
    };
}
